import { isAwxSubdomain } from './getSubdomain';

/**
 * Helper to extract country name from string or address object.
 */
export const getCountryName = (field: any): string => {
  if (!field) return '';
  if (typeof field === 'string') return field.trim();
  if (typeof field === 'object') {
    if (typeof field.country === 'string') return field.country.trim();
    if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
      return field.country.name.trim();
    }
    if (typeof field.name === 'string') return field.name.trim();
  }
  return '';
};

/**
 * Checks whether a shipment is an export shipment (i.e. destination or origin differs from organization country).
 * Centralizes the exact export logic previously embedded in ProductDescription/index.tsx.
 */
export function isExportShipment({
  orgCountryName,
  shipper,
  consignee,
  shipmentById,
}: {
  orgCountryName?: string;
  shipper?: any;
  consignee?: any;
  shipmentById?: any;
}): boolean {
  const shipperCountry = getCountryName(shipper) || getCountryName(shipmentById?._shipperId);
  const consigneeCountry = getCountryName(consignee) || getCountryName(shipmentById?._consigneeId);

  let result = false;

  // 1. If both shipper and consignee countries are known and differ, it's definitively an export/international shipment
  if (shipperCountry && consigneeCountry && shipperCountry.toLowerCase() !== consigneeCountry.toLowerCase()) {
    result = true;
  } else {
    // 2. Check against organization country (fallback to shipperCountry or 'India' if org data not yet loaded)
    const effectiveOrgCountry = (orgCountryName || shipperCountry || 'India').trim().toLowerCase();
    const dest = (consigneeCountry || '').trim().toLowerCase();
    const orig = (shipperCountry || '').trim().toLowerCase();

    if (dest && dest !== effectiveOrgCountry) {
      result = true;
    } else if (orig && orig !== effectiveOrgCountry) {
      result = true;
    }
  }

  return result;
}

/**
 * Single source of truth for whether HSN Code is required in Box Dimensions.
 * Rule:
 *   (isExportShipment || subdomain === 'awx') && shipmentType !== 'document'
 * Document shipment type overrides both export and AWX rules.
 */
export function isHsnCodeRequired({
  isShipmentExport,
  subdomain,
  customHostname,
  shipmentType,
}: {
  isShipmentExport: boolean;
  subdomain?: string;
  customHostname?: string;
  shipmentType?: string | { type?: string };
}): boolean {
  const typeStr = typeof shipmentType === 'string' ? shipmentType : shipmentType?.type;
  const isDocument = (typeStr || '').trim().toLowerCase() === 'document';
  if (isDocument) {
    return false;
  }
  const isAwx = subdomain ? subdomain.toLowerCase() === 'awx' : isAwxSubdomain(customHostname);
  return Boolean(isShipmentExport || isAwx);
}
